import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TokenGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const OtpVerification = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", ""]);
  const [formData, setFormData] = useState({
    code: "",
  });
  const refs = useRef([]);

  const handleChange = (index, value) => {
    const newOtpValues = [...otpValues];

    if (value.length === 0 && newOtpValues[index] !== "") {
      newOtpValues[index] = "";
      setOtpValues(newOtpValues);
      if (index > 0) {
        refs.current[index - 1].focus();
      }
      return;
    }

    if (value.length > 1) return;

    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    const otpCode = newOtpValues.join("");
    setFormData({ ...formData, code: otpCode });

    if (value && index < 4) {
      refs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData("text").slice(0, 5);
    if (pastedData.length === 5 && /^\d+$/.test(pastedData)) {
      const newOtpValues = pastedData.split("");
      setOtpValues(newOtpValues);

      setFormData({ ...formData, code: pastedData });

      refs.current[4].focus();
    } else {
      toast.error("Invalid OTP format pasted. Please paste a 5-digit code.");
    }
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();

    if (formData.code.length !== 5) {
      toast.error("Please enter the complete 5-digit OTP.");
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${StandardURL}/iNyumba/auth/otp`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.response.token) {
        localStorage.setItem("token", response.data.response.token);
        localStorage.setItem(
          "user",
          JSON.stringify(response.data.response.user)
        );
      }

      toast.success("OTP verification successful!");
      setIsLoading(false);
      navigate("../Landlord/home");
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      toast.error(error.response?.data?.response || "OTP verification failed. Please try again.");
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://backend.inyumba.co.ke/iNyumba/auth/resend-otp",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsLoading(false);
      toast.success("OTP resent successfully!");
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      toast.error(error.response?.data?.response || "Failed to resend OTP. Please try again.");
    }
  };

  return (
    <TokenGuard>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl border border-gray-200 transform hover:scale-105 transition-all duration-300 ease-in-out">
          <div className="text-center">
            <h2 className="mt-2 text-4xl font-extrabold text-gray-900 leading-tight">
              Verify Your Account 🔐
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please enter the 5-digit code sent to your email address.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="flex justify-center space-x-3 sm:space-x-4">
              {otpValues.map((value, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  ref={(el) => (refs.current[index] = el)}
                  className="w-14 h-14 text-center text-3xl font-bold border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                  pattern="[0-9]"
                  inputMode="numeric"
                />
              ))}
            </div>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mt-6">
              <button
                type="submit"
                className={`
                  relative flex-1 justify-center py-2.5 px-4 border border-transparent text-lg font-semibold rounded-lg shadow-md
                  ${isLoading || formData.code.length !== 5
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all duration-200 ease-in-out hover:scale-105"
                  }
                  text-white
                `}
                disabled={isLoading || formData.code.length !== 5}
              >
                {isLoading && (
                  <Oval
                    height={24}
                    width={24}
                    color="white"
                    visible={true}
                    ariaLabel='oval-loading'
                    secondaryColor="rgba(255,255,255,0.4)"
                    strokeWidth={5}
                    strokeWidthSecondary={5}
                    wrapperClass="absolute inset-0 flex items-center justify-center"
                  />
                )}
                <span className={isLoading ? "opacity-0" : ""}>Verify OTP</span> 
              </button>
              <button
                type="button"
                onClick={handleResendOtp}
                className={`
                  relative flex-1 justify-center py-2.5 px-4 border border-indigo-600 text-lg font-semibold rounded-lg shadow-md
                  ${isLoading
                    ? "text-indigo-400 cursor-not-allowed border-indigo-400"
                    : "text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all duration-200 ease-in-out hover:scale-105"
                  }
                `}
                disabled={isLoading}
              >
                {isLoading && (
                  <Oval
                    height={24}
                    width={24}
                    color="#6366F1"
                    visible={true}
                    ariaLabel='oval-loading'
                    secondaryColor="rgba(99,102,241,0.4)"
                    strokeWidth={5}
                    strokeWidthSecondary={5}
                    wrapperClass="absolute inset-0 flex items-center justify-center"
                  />
                )}
                <span className={isLoading ? "opacity-0" : ""}>Resend OTP</span> 
              </button>
            </div>
          </form>
        </div>
      </div>
    </TokenGuard>
  );
};

export default OtpVerification;